import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Home06Icon as Home06IconStroke,
  Music02Icon as Music02IconStroke,
  ChartIcon as ChartIconStroke,
  MenuSquareIcon as MenuSquareIconStroke,
} from "@hugeicons-pro/core-stroke-rounded";
import {
  Home06Icon as Home06IconSolid,
  Music02Icon as Music02IconSolid,
  ChartIcon as ChartIconSolid,
  MenuSquareIcon as MenuSquareIconSolid,
} from "@hugeicons-pro/core-solid-rounded";
import { useAuth } from "@/contexts/AuthContext";
import { useMusic } from "@/contexts/MusicContext";
import { useOptimizedProfileData } from "@/hooks/profile/useOptimizedProfileData";
import { useSidebarActiveContext } from "@/contexts/SidebarActiveContext";
import { getSidebarSection } from "@/utils/sidebarShellRoutes";
import type { PublicSidebarSection } from "./PublicSidebar";
import { MobileAccountSheet } from "./account-menu/MobileAccountSheet";
import { PublicMenuTray } from "./PublicMenuTray";
import { BrandLogoMark } from "./BrandLogoMark";
import { hapticFeedback, isTouchDevice } from "@/utils/mobileInteractions";

type HugeIcon = typeof MenuSquareIconStroke;

const TABS: {
  section: PublicSidebarSection;
  label: string;
  to: string;
  icon: { stroke: HugeIcon; solid: HugeIcon };
}[] = [
  {
    section: "home",
    label: "Home",
    to: "/",
    icon: { stroke: Home06IconStroke, solid: Home06IconSolid },
  },
  {
    section: "music",
    label: "Music",
    to: "/songs",
    icon: { stroke: Music02IconStroke, solid: Music02IconSolid },
  },
  {
    section: "charts",
    label: "Charts",
    to: "/charts",
    icon: { stroke: ChartIconStroke, solid: ChartIconSolid },
  },
];

interface PublicMobileBottomBarProps {
  // Highlights the "You" tab instead of a PublicSidebarSection — used by
  // account-scoped pages (e.g. My Support Tickets) that aren't part of the
  // public sidebar shell and so have no section of their own to match.
  youActive?: boolean;
}

// Top accent hanging from the bar's upper edge, marking the active tab —
// screen 54a's bar-item spec.
function ActiveTab() {
  return (
    <span className="absolute left-1/2 top-0 h-[3px] w-7 -translate-x-1/2 rounded-b-[3px] bg-[#E9B949]" />
  );
}

// Mobile/tablet nav for the public sidebar-shell pages (screens 32a-32e,
// restyled per 54a): on mobile a full-width 56px dark bar, on tablet a
// floating centered dark dock. Desktop (lg:+) keeps PublicSidebar
// untouched — this never renders there.
export function PublicMobileBottomBar({ youActive = false }: PublicMobileBottomBarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { profile } = useOptimizedProfileData(user?.id);
  const { overrideActive } = useSidebarActiveContext();
  const { currentTrack } = useMusic();
  const [menuOpen, setMenuOpen] = useState(false);

  const active = overrideActive ?? getSidebarSection(location.pathname);

  const tap = () => {
    if (isTouchDevice()) hapticFeedback.light();
  };

  const tabClass =
    "relative flex flex-1 md:flex-none md:w-[104px] h-14 md:h-16 flex-col items-center justify-center gap-1 transition-transform active:scale-95";

  const iconColor = (isActive: boolean) =>
    isActive ? "#FFFFFF" : "rgba(255,255,255,.62)";

  const labelClass = (isActive: boolean) =>
    `font-archivo text-[10px] leading-none tracking-[.01em] ${
      isActive ? "font-bold text-white" : "font-medium text-white/[.62]"
    }`;

  const avatarRing = (isActive: boolean) =>
    `rounded-full bg-[#2A2660] object-cover ring-2 ${
      isActive ? "ring-white" : "ring-white/[.62]"
    }`;

  return (
    <>
      <nav
        className={`lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-[#1E1B4B] pb-[env(safe-area-inset-bottom)] md:inset-x-0 md:left-1/2 md:w-fit md:-translate-x-1/2 md:rounded-[12px] md:pb-0 md:shadow-[0_10px_28px_rgba(6,4,24,0.4)] ${
          currentTrack ? "md:bottom-28" : "md:bottom-6"
        }`}
        aria-label="Primary"
      >
        <div className="flex items-stretch md:px-2">
          {TABS.map((tab) => {
            const isActive = active === tab.section;
            const Icon = isActive ? tab.icon.solid : tab.icon.stroke;
            return (
              <Link key={tab.section} to={tab.to} onClick={tap} className={tabClass}>
                {isActive && <ActiveTab />}
                <HugeiconsIcon icon={Icon} size={24} color={iconColor(isActive)} />
                <span className={labelClass(isActive)}>{tab.label}</span>
              </Link>
            );
          })}

          <button
            type="button"
            aria-expanded={menuOpen}
            onClick={() => {
              tap();
              setMenuOpen((v) => !v);
            }}
            className={tabClass}
          >
            {menuOpen && <ActiveTab />}
            <HugeiconsIcon
              icon={menuOpen ? MenuSquareIconSolid : MenuSquareIconStroke}
              size={24}
              color={iconColor(menuOpen)}
            />
            <span className={labelClass(menuOpen)}>Menu</span>
          </button>

          {user ? (
            <MobileAccountSheet
              renderTrigger={(onClick) => (
                <button
                  type="button"
                  onClick={() => {
                    tap();
                    onClick();
                  }}
                  className={tabClass}
                >
                  {youActive && <ActiveTab />}
                  <img
                    src={profile?.avatar_url || "/placeholder.svg"}
                    alt="Account"
                    className={`h-6 w-6 ${avatarRing(youActive)}`}
                  />
                  <span className={labelClass(youActive)}>You</span>
                </button>
              )}
            />
          ) : (
            <button
              type="button"
              onClick={() => {
                tap();
                navigate("/login");
              }}
              className={tabClass}
            >
              <BrandLogoMark className={`h-6 w-6 ${avatarRing(false)}`} />
              <span className={labelClass(false)}>You</span>
            </button>
          )}
        </div>
      </nav>

      <PublicMenuTray
        open={menuOpen}
        onOpenChange={setMenuOpen}
        active={active}
      />
    </>
  );
}
